/**
 * The PrimeUI Community licence key.
 *
 * @why    Committed, and deliberately not an environment variable or a `.env` file.
 *
 *         This value is NOT a secret. PrimeVue takes it through
 *         `app.use(PrimeVue, { license })`, which means webpack inlines it into the bundle
 *         every tablet downloads — anyone who opens the app can read it out of
 *         `dist/js/app.js`. Hiding it in the build pipeline buys nothing and costs a lot:
 *         this repo has no `amplify.yml`, so an env var would have to be set by hand in the
 *         AWS console for production, and every local build, the demo build and the visual
 *         harness would silently render the "Invalid PrimeUI License" banner until each
 *         machine grew its own `.env.local`.
 *
 *         And that banner is not cosmetic. Measured at 1280x800: a class-less `div`,
 *         `position: fixed`, `z-index: 2147483647` (above even Intercom), 175x36 px at
 *         (1089, 748) — which covers 159x36 px of the "Analizar" button, 34% of it. It has
 *         `pointer-events: none` so it steals no taps, but a kitchen should never see it.
 *
 *         What the licence DOES forbid is tampering with the verifier or hiding the banner.
 *         Publishing the token is not that. If PrimeUI ever objects, the fallback is an env
 *         var plus an Amplify console variable — and someone owns keeping them in sync.
 *
 * @rule   Rotating it is a commit, not a console change. The Community licence runs 12 months
 *         plus 30 days of grace, so that happens about once a year.
 * @rule   And every PrimeVue upgrade re-checks it. PrimeVue verifies the token against its OWN
 *         release date (`RELEASE_DATE` in `@primevue/core/config`, 2026-08-13 in 5.0.1): a
 *         version published after this token's expiry paints the banner on every tablet at
 *         once, however valid the token still is. `tests/unit/ui.install.spec.js` fails first.
 *
 * Verified against `@primeui/license-manager` on 2026-09-22 — `registerLicense({ primeui })`
 * then `verifyLicense('primeui', { releaseDate })`:
 *
 *   status `active`, issued 2026-08-05, expires **2027-08-05**, 316 days left.
 *
 * What a skewed device clock does to it, also measured, because that is the one failure this
 * key cannot prevent:  +300 days `active` · +320 days `grace` (still valid, warns) · +366 days
 * `expired`, and the banner returns — on that tablet only.
 *
 * Two conditions that come with this licence and are not enforced here:
 *   - The verifier computes validity from the DEVICE's `Date.now()`, so a tablet with a
 *     skewed clock can believe the licence expired and paint the banner on that tablet only.
 *     `install.js` reports `status` and `daysUntilExpiry` to PostHog for exactly this.
 *   - Community covers 4 seats, and does NOT cover the `primeui-pro:*` products — which
 *     `Chart` and `Editor` become in v6. That matters for the Hub, not for the Tracker.
 */
export const PRIMEUI_LICENSE = 'eyJpZCI6ImVlNWY1YzhiLTUzMzYtNDcyMC05MTNiLTM1NzRjMTE1ZTc5NCIsInByb2R1Y3QiOiJwcmltZXVpIiwidGllciI6ImNvbW11bml0eSIsInR5cGUiOiJkZXYiLCJpYXQiOjE3ODU5MjczNjgsImV4cCI6MTgxNzQ2MzM2OH0._z5UzkiA6pwycLONyVxsSXuPN1pAYUEGweik4FSkSKFa2pMlHD1brrqm71ESN1s7pTwBB4HQawwyKu-u4_b_BA';

/** What the licence's own verifier allows past `exp`, for Community keys (its `GRACE_DAYS`). */
const GRACE_DAYS = 30;
const DAY_MS = 86_400_000;

/**
 * The licence's status and days left, from the token's own expiry and THIS device's clock.
 *
 * The same arithmetic as `@primeui/license-manager` for a Community key (active, then 30 days
 * of grace, then expired), without calling it: the verifier is not a dependency of this app —
 * PrimeVue brings it in — and PrimeVue has already verified the signature, which is what
 * `licenseValid` reports. Reading the payload is reading our own key, not touching the verifier.
 *
 * @param {string} token
 * @param {number} [now] Milliseconds, the device clock by default.
 * @returns {{ licenseStatus: 'active' | 'grace' | 'expired' | 'invalid', licenseDaysUntilExpiry: number | null }}
 */
export function licenseExpiry(token, now = Date.now()) {
    try {
        const body = token.split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
        const { exp } = JSON.parse(atob(body.padEnd(Math.ceil(body.length / 4) * 4, '=')));
        if (!Number.isFinite(exp)) throw new Error('no exp');
        const expiresAt = exp * 1000;
        const licenseStatus = now > expiresAt + GRACE_DAYS * DAY_MS ? 'expired' : now > expiresAt ? 'grace' : 'active';
        return { licenseStatus, licenseDaysUntilExpiry: Math.floor((expiresAt - now) / DAY_MS) };
    } catch (_) {
        return { licenseStatus: 'invalid', licenseDaysUntilExpiry: null };
    }
}
